import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  resolveSnackbarDurationMs,
  SnackbarData,
} from '../components/snackbar/snackbar.model';
import { SnackbarStackComponent } from '../components/snackbar/snackbar-stack.component';

/** Must match `.app-snackbar-stack__item--leave` transition duration. */
export const SNACKBAR_LEAVE_ANIMATION_MS = 200;

export interface SnackbarStackItem {
  id: string;
  data: SnackbarData;
}

export interface SnackbarRef {
  dismiss(): void;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly document = inject(DOCUMENT);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly matDialog = inject(MatDialog, { optional: true });

  private readonly stackSignal = signal<SnackbarStackItem[]>([]);
  private readonly dismissTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private stackRef?: ComponentRef<SnackbarStackComponent>;
  private closePopoverTimeout?: ReturnType<typeof setTimeout>;

  readonly stack = this.stackSignal.asReadonly();

  constructor() {
    // Dialogs also use the Popover top layer; last showPopover wins. When a
    // dialog opens while snackbars are visible, re-open the stack so it stays
    // above the new dialog (e.g. CAP Alerts enable → manage).
    this.matDialog?.afterOpened.subscribe(() => {
      if (this.stackSignal().length === 0) {
        return;
      }

      this.openPopover();
    });
  }

  show(data: SnackbarData): SnackbarRef {
    this.ensureStackMounted();
    this.openPopover();

    const id = this.createId();
    const item: SnackbarStackItem = { id, data };
    this.stackSignal.update((items) => [...items, item]);

    const durationMs = resolveSnackbarDurationMs(data.duration);

    if (durationMs !== null) {
      const timeoutId = setTimeout(() => this.dismiss(id), durationMs);
      this.dismissTimeouts.set(id, timeoutId);
    }

    return {
      dismiss: () => this.dismiss(id),
    };
  }

  dismiss(id: string): void {
    const timeoutId = this.dismissTimeouts.get(id);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.dismissTimeouts.delete(id);
    }

    this.stackSignal.update((items) => items.filter((item) => item.id !== id));

    if (this.stackSignal().length === 0) {
      this.closePopoverAfterLeave();
    }
  }

  private ensureStackMounted(): void {
    if (this.stackRef) {
      return;
    }

    this.stackRef = createComponent(SnackbarStackComponent, {
      environmentInjector: this.environmentInjector,
    });

    this.applicationRef.attachView(this.stackRef.hostView);
    this.document.body.appendChild(this.stackRef.location.nativeElement);
    this.stackRef.changeDetectorRef.detectChanges();
  }

  /**
   * CDK 21+ dialogs live in the CSS top layer (Popover API). Re-open on every
   * show (and when a dialog opens while the stack is visible) so snackbars
   * stay above dialogs — last showPopover wins in the top layer.
   */
  private openPopover(): void {
    if (this.closePopoverTimeout) {
      clearTimeout(this.closePopoverTimeout);
      this.closePopoverTimeout = undefined;
    }

    const element = this.stackRef?.location.nativeElement as
      | HTMLElement
      | undefined;

    if (!element || !('showPopover' in element)) {
      return;
    }

    element.setAttribute('popover', 'manual');

    try {
      element.hidePopover();
    } catch {
      // Not open yet.
    }

    try {
      element.showPopover();
    } catch {
      // Unsupported in this environment.
    }
  }

  /** Stay in the top layer until the leave animation finishes, then leave it. */
  private closePopoverAfterLeave(): void {
    const element = this.stackRef?.location.nativeElement as
      | HTMLElement
      | undefined;

    if (
      !element ||
      !('hidePopover' in element) ||
      !element.hasAttribute('popover')
    ) {
      return;
    }

    if (this.closePopoverTimeout) {
      clearTimeout(this.closePopoverTimeout);
    }

    this.closePopoverTimeout = setTimeout(() => {
      this.closePopoverTimeout = undefined;

      if (this.stackSignal().length > 0) {
        return;
      }

      try {
        element.hidePopover();
      } catch {
        // Already closed.
      }
    }, SNACKBAR_LEAVE_ANIMATION_MS);
  }

  private createId(): string {
    return `snackbar-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}
